import type { HomeCity } from '@/state/types';

/**
 * Nominatim client with country filter for D-A-CH.
 * - Rate-limited: caller should debounce to ~400 ms.
 * - Browser-side fetch cannot set the User-Agent header (forbidden in browsers);
 *   Nominatim therefore identifies us via the Referer automatically set by the browser.
 * - All requests sent with `Accept-Language: de` to get German place names.
 */

const ENDPOINT = 'https://nominatim.openstreetmap.org/search';

interface NominatimAddress {
  city?: string;
  town?: string;
  village?: string;
  hamlet?: string;
  municipality?: string;
  state?: string;
  county?: string;
  country?: string;
  country_code?: string;
}

interface NominatimResult {
  place_id: number;
  display_name: string;
  lat: string;
  lon: string;
  address?: NominatimAddress;
  type?: string;
  class?: string;
  /** Phase 12.1.3 — Nominatim ranks results 0..1; we keep the top per dedupe key. */
  importance?: number;
}

export async function searchPlaces(
  query: string,
  signal?: AbortSignal,
): Promise<HomeCity[]> {
  const trimmed = query.trim();
  if (trimmed.length < 2) return [];

  // Phase 12.1.3 — `dedupe=1` lets Nominatim collapse exact-match duplicates
  // server-side (we still de-dupe client-side as a second layer).
  const params = new URLSearchParams({
    q: trimmed,
    format: 'json',
    countrycodes: 'de,at,ch',
    addressdetails: '1',
    dedupe: '1',
    limit: '8',
  });

  const res = await fetch(`${ENDPOINT}?${params.toString()}`, {
    signal,
    headers: {
      Accept: 'application/json',
      'Accept-Language': 'de',
    },
  });

  if (!res.ok) {
    throw new Error(`Nominatim error: HTTP ${res.status}`);
  }

  const raw = (await res.json()) as NominatimResult[];
  // Phase 12.1.3 — Skip administrative boundaries (Landkreise, Forste, …) that
  // aren't usable as a player home city.
  const playable = raw.filter((r) => isPlayablePlace(r));
  return playable.map(toHomeCity).filter((c): c is HomeCity => c !== null);
}

/**
 * Returns true for OSM features that look like a settlement (city/town/
 * village/hamlet/municipality). Excludes admin boundaries like
 * "Landkreis Garmisch-Partenkirchen" or "Ettaler Forst".
 */
function isPlayablePlace(r: NominatimResult): boolean {
  const cls = r.class ?? '';
  const t = r.type ?? '';
  if (cls === 'place') {
    return ['city', 'town', 'village', 'hamlet', 'municipality', 'suburb'].includes(t);
  }
  if (cls === 'boundary') {
    // "administrative" can be both a Stadt and a Landkreis. Heuristic: keep
    // when the address resolves to a settled place (city/town/village exists);
    // drop pure Landkreis-/Forst-features.
    if (t === 'administrative') {
      const a = r.address ?? {};
      return Boolean(a.city || a.town || a.village || a.hamlet || a.municipality);
    }
  }
  return false;
}

function toHomeCity(r: NominatimResult): HomeCity | null {
  const lat = Number(r.lat);
  const lng = Number(r.lon);
  if (!Number.isFinite(lat) || !Number.isFinite(lng)) return null;

  const addr = r.address ?? {};
  const placeName =
    addr.city ??
    addr.town ??
    addr.village ??
    addr.hamlet ??
    addr.municipality ??
    r.display_name.split(',')[0] ??
    'Unbenannt';

  return {
    displayName: placeName,
    center: { lat, lng },
    country: addr.country ?? '',
    state: addr.state ?? addr.county ?? '',
  };
}
